import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { useHoverTip } from '../../atoms/Tooltip/useHoverTip';
import styles from './MessageActions.module.css';

/**
 * Ported from spectrum-ui's `MessageActions` block — the unmodified source sits
 * in `reference/spectrum-ui/ai-assistants/` for diffing. Upstream is Tailwind +
 * lucide-react; nothing of either survives here.
 *
 * All five actions are ported: copy, regenerate, the two thumbs, and branch.
 * Branch is conditional on `onBranch` exactly as upstream has it — there is
 * nothing sensible for the button to do without a handler.
 *
 * The action buttons are real `Button` atoms — `Hierarchy=Ghost Grey`, icon
 * only — rather than a local copy. Upstream hand-rolls the surface, the hover
 * fill, the focus ring and the press dip; every one of those already lives in
 * `Button` and is bound to the same tokens the Figma set binds, so a local copy
 * would be a second source of truth that drifts the first time Button is
 * retokenised. `PromptComposer` made the same call for its send control.
 *
 * That makes the buttons 20x20 on a 12px glyph, which is Reef's ghost-button
 * geometry, where upstream draws 28x28 on 14px.
 *
 * Figma: `Message Actions` (838:25588).
 */

/** Upstream's `MessageFeedback`. */
export type MessageFeedback = 'positive' | 'negative';

/** Figma `Variant`. */
export type MessageActionsVariant = 'ghost' | 'pill';

export type MessageActionsProps = {
  /** What `copy` writes to the clipboard. */
  content?: string;
  /**
   * Controlled rating. Omit to let the component hold its own; pass `null` for
   * explicitly unrated.
   */
  feedback?: MessageFeedback | null;
  /** Fires only after the clipboard write actually succeeded — see `copy` below. */
  onCopy?: (content: string) => void;
  onRegenerate?: () => void;
  /** Receives `null` when the user clears a rating by pressing it again. */
  onFeedback?: (feedback: MessageFeedback | null) => void;
  /** Supplying this is what renders the branch action, as upstream does. */
  onBranch?: () => void;
  /**
   * Supplying this renders "Ask again in Expert mode" — DeepDive AI's offer on
   * an answer the fast path gave (v2's "Deeper analysis"). Not in upstream or
   * Figma; it follows the branch action's pattern, an extra icon at the end.
   */
  onAskExpert?: () => void;
  /**
   * Upstream's reveal-on-hover, which is off by default here and on there.
   *
   * Upstream hangs it on a `group/message` wrapper. This system has no Message
   * component, so a toolbar that defaults to `opacity: 0` would be invisible
   * with nothing able to reveal it. Opt in and put `data-message` on whatever
   * wraps the message; the toolbar then appears on hovering that, and on its own
   * focus, and stays visible on touch where there is no hover to key off.
   */
  revealOnHover?: boolean;
  variant?: MessageActionsVariant;
  className?: string;
};

/**
 * How long the check holds before it fades back to the copy glyph. A dwell time,
 * not a transition — every `--duration-*` token is under 300ms, so there is no
 * variable this belongs to.
 */
const COPIED_DWELL_MS = 1600;

type Action = {
  key: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  pressed?: boolean;
};

export function MessageActions({
  content = '',
  feedback: feedbackProp,
  onCopy,
  onRegenerate,
  onFeedback,
  onBranch,
  onAskExpert,
  revealOnHover = false,
  variant = 'ghost',
  className,
}: MessageActionsProps) {
  const [copied, setCopied] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [ownFeedback, setOwnFeedback] = useState<MessageFeedback | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  const toolbar = useRef<HTMLDivElement>(null);
  const dwell = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /* Each action's name over it on hover and focus — not `title`, which waits a second. */
  const tip = useHoverTip();

  useEffect(() => () => clearTimeout(dwell.current), []);

  const feedback = feedbackProp !== undefined ? feedbackProp : ownFeedback;
  const positive = feedback === 'positive';
  const negative = feedback === 'negative';

  async function copy() {
    /*
     * Upstream swallows the failure and shows "Copied" anyway. `writeText`
     * rejects on an insecure origin, on a denied permission, and in any browser
     * without the API — so upstream tells the user their text is on the
     * clipboard when it is not. Nothing is claimed here unless the write
     * resolved, and `onCopy` fires on the same condition so a host cannot log a
     * copy that did not happen.
     */
    try {
      await navigator.clipboard.writeText(content);
    } catch {
      return;
    }
    onCopy?.(content);
    setCopied(true);
    clearTimeout(dwell.current);
    dwell.current = setTimeout(() => setCopied(false), COPIED_DWELL_MS);
  }

  function rate(next: MessageFeedback) {
    const value = feedback === next ? null : next;
    setOwnFeedback(value);
    onFeedback?.(value);
  }

  /*
   * A rated thumb swaps to the filled glyph, darkens and grows a little —
   * upstream's `fill-current`, `text-neutral-800` and `scale-110`. The wrapper
   * span is load-bearing for the colour; see `.glyph` in the stylesheet.
   */
  const thumb = (name: 'thumbsUp' | 'thumbsDown', rated: boolean) => (
    <span className={styles.glyph}>
      <Icon
        name={rated ? (`${name}Fill` as 'thumbsUpFill' | 'thumbsDownFill') : name}
        className={rated ? styles.rated : undefined}
      />
    </span>
  );

  const actions: Action[] = [
    {
      key: 'copy',
      label: copied ? 'Copied' : 'Copy message',
      onClick: copy,
      icon:
        (
          /*
           * Both glyphs are stacked and cross-faded rather than swapped, so the
           * button never reflows mid-transition. `--enter-blur` is the token for
           * "not settled yet" and is upstream's 2px exactly.
           */
          <span className={styles.copyStack}>
            <Icon name="copy" className={copied ? styles.glyphOut : styles.glyphIn} />
            <Icon
              name="check"
              className={[styles.check, copied ? styles.glyphIn : styles.glyphOut].join(' ')}
            />
          </span>
        ),
    },
    {
      key: 'regenerate',
      label: 'Regenerate',
      onClick: () => {
        setSpinning(true);
        onRegenerate?.();
      },
      icon:
        (
          /*
           * The half-turn is reset by the transition ending rather than by a
           * timer, so the spin stays in step with `--duration-slow` instead of
           * repeating its value as a magic number in JS. Reduced motion collapses
           * the token to 0.01ms, which still fires `transitionend` — that is why
           * the collapsed value is not zero.
           */
          <span
            className={[styles.regenerate, spinning ? styles.spinning : null]
              .filter(Boolean)
              .join(' ')}
            onTransitionEnd={() => setSpinning(false)}
          >
            <Icon name="regenerate" />
          </span>
        ),
    },
    {
      key: 'positive',
      label: 'Good response',
      pressed: positive,
      onClick: () => rate('positive'),
      icon: thumb('thumbsUp', positive),
    },
    {
      key: 'negative',
      label: 'Bad response',
      pressed: negative,
      onClick: () => rate('negative'),
      icon: thumb('thumbsDown', negative),
    },
  ];

  if (onAskExpert) {
    actions.push({
      key: 'ask-expert',
      label: 'Ask again in Expert mode',
      onClick: onAskExpert,
      icon: (
        <span className={styles.glyph}>
          <Icon name="aiSpark" />
        </span>
      ),
    });
  }

  if (onBranch) {
    actions.push({
      key: 'branch',
      label: 'Branch from here',
      onClick: onBranch,
      icon: (
        <span className={styles.glyph}>
          <Icon name="gitBranch" />
        </span>
      ),
    });
  }

  /*
   * `role="toolbar"` is a promise: assistive tech expects one tab stop for the
   * whole group and the arrow keys to move within it. Upstream declares the role
   * and implements none of it, which leaves a keyboard user tabbing through five
   * controls that announce themselves as one. Not in Figma — Figma has no
   * concept of focus order.
   */
  const moveFocus = useCallback((to: number) => {
    const buttons = toolbar.current?.querySelectorAll('button');
    if (!buttons || buttons.length === 0) return;
    const index = ((to % buttons.length) + buttons.length) % buttons.length;
    setFocusIndex(index);
    buttons[index].focus();
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const count = toolbar.current?.querySelectorAll('button').length ?? 0;
    if (count === 0) return;
    switch (event.key) {
      case 'Escape':
        tip.hide();
        break;
      case 'ArrowRight':
        event.preventDefault();
        moveFocus(focusIndex + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus(focusIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(0);
        break;
      case 'End':
        event.preventDefault();
        moveFocus(count - 1);
        break;
      default:
        break;
    }
  }

  /*
   * Every button but one leaves the tab order. Clamped because the branch action
   * can disappear while it holds focus, which would otherwise leave the toolbar
   * with no tab stop at all.
   */
  const active = Math.min(focusIndex, actions.length - 1);

  return (
    <div
      ref={toolbar}
      role="toolbar"
      aria-label="Message actions"
      {...tip.group}
      onKeyDown={onKeyDown}
      className={[
        styles.toolbar,
        variant === 'pill' ? styles.pill : null,
        revealOnHover ? styles.reveal : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {actions.map((action, index) => (
        <Button
          key={action.key}
          hierarchy="ghostGrey"
          aria-label={action.label}
          aria-pressed={action.pressed}
          tabIndex={index === active ? 0 : -1}
          onClick={action.onClick}
          iconLeading={action.icon}
        />
      ))}
      {tip.bubble}
    </div>
  );
}
